import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpClient } from '@angular/common/http';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { loginStart, authentificateSuccess, authentificateFail, autoLogin, autoLoginFinished, logout, orgoLoginStart, orgoCallbackStart } from './auth.actions';
import { resetGdprConsentCache } from '../gdpr.guard';
import { User } from '../user.model';
import { AuthService } from '../auth.service';

export interface AuthResponseData {
  user_id: string;
  username: string;
  email: string;
  token: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  groups?: string[];
}

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private http = inject(HttpClient);
  private router = inject(Router);
  private authService = inject(AuthService);
  private apiUrl = environment.apiUrl;

  private handleAuthentication = (
    expiresIn: number,
    username: string,
    userId: string,
    token: string,
    isStaff: boolean = false,
    isSuperuser: boolean = false,
    groups: string[] = []
  ) => {
    const redirect = true;
    const expirationDate = new Date(new Date().getTime() + expiresIn * 1000);
    const user = new User(username, userId, token, expirationDate, isStaff, isSuperuser, groups);
    localStorage.setItem('userData', JSON.stringify(user));

    return authentificateSuccess({
      username,
      userId,
      token,
      expirationDate,
      redirect,
      isStaff,
      isSuperuser,
      groups,
    });
  };

  private handleError = (errorRes: any) => {
    let errorMessage = 'A apărut o eroare necunoscută!';
    if (!errorRes.error) {
      return of(authentificateFail({ payload: errorMessage }));
    }

    if (errorRes.error.non_field_errors) {
      errorMessage = errorRes.error.non_field_errors[0];
    } else if (errorRes.error.detail) {
      errorMessage = errorRes.error.detail;
    } else if (typeof errorRes.error === 'object') {
      const errorKeys = Object.keys(errorRes.error);
      if (errorKeys.length > 0) {
        const firstKey = errorKeys[0];
        const errorVal = errorRes.error[firstKey];
        if (Array.isArray(errorVal)) {
          errorMessage = `${firstKey}: ${errorVal[0]}`;
        } else if (typeof errorVal === 'string') {
          errorMessage = errorVal;
        }
      }
    }

    // Map common English messages to Romanian if they come from DRF defaults
    if (errorMessage === 'Unable to log in with provided credentials.') {
      errorMessage = 'Utilizator sau parolă incorectă.';
    }

    return of(authentificateFail({ payload: errorMessage }));
  };

  authLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginStart),
      switchMap(({ username, password }) =>
        this.http
          .post<AuthResponseData>(this.apiUrl + '/login/', {
            username,
            password,
            returnSecureToken: true,
          })
          .pipe(
            tap((resData) => {
              console.log(resData);

              /*this.authService.setLogoutTimer(+resData.expiresIn * 1000)*/ this.authService.setLogoutTimer(
                99900 * 1000
              );
            }),
            map((resData) =>
              this.handleAuthentication(
                99900000,
                resData.username,
                resData.user_id,
                resData.token,
                resData.is_staff,
                resData.is_superuser,
                resData.groups
              )
            ),
            catchError(this.handleError)
          )
      )
    )
  );

  authRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authentificateSuccess),
        tap(({ redirect }) => {
          if (redirect) {
            this.router.navigate(['/']);
          }
        })
      ),
    { dispatch: false }
  );

  autoLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(autoLogin),
      map(() => {
        const userData: {
          username: string;
          id: string;
          _token: string;
          _tokenExpirationDate: string;
          isStaff?: boolean;
          isSuperuser?: boolean;
          groups?: string[];
        } = JSON.parse(localStorage.getItem('userData') || '{}');

        if (!userData?.username) {
          return autoLoginFinished();
        }

        const loadedUser = new User(
          userData.username,
          userData.id,
          userData._token,
          new Date(userData._tokenExpirationDate),
          userData.isStaff || false,
          userData.isSuperuser || false,
          userData.groups || []
        );

        if (!loadedUser.token) {
          return autoLoginFinished();
        }

        const expirationDuration = new Date(userData._tokenExpirationDate).getTime() - new Date().getTime();
        this.authService.setLogoutTimer(expirationDuration);

        return authentificateSuccess({
          username: loadedUser.username,
          userId: loadedUser.id,
          token: loadedUser.token,
          expirationDate: new Date(userData._tokenExpirationDate),
          redirect: false,
          isStaff: loadedUser.isStaff,
          isSuperuser: loadedUser.isSuperuser,
          groups: loadedUser.groups,
        });
      })
    )
  );

  orgoLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(orgoLoginStart),
      switchMap(() =>
        this.http
          .post<{ login_url: string }>(this.apiUrl + '/orgo/initiate/', {})
          .pipe(
            tap((res) => {
              window.location.href = res.login_url;
            }),
            // This effect redirects away, so no action is dispatched
            switchMap(() => []),
            catchError(this.handleError)
          )
      )
    )
  );

  orgoCallback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(orgoCallbackStart),
      switchMap(({ successToken }) =>
        this.http
          .post<AuthResponseData>(this.apiUrl + '/orgo/callback/', {
            success_token: successToken,
          })
          .pipe(
            tap(() => {
              this.authService.setLogoutTimer(99900 * 1000);
            }),
            map((resData) =>
              this.handleAuthentication(
                99900000,
                resData.username,
                resData.user_id,
                resData.token,
                resData.is_staff,
                resData.is_superuser,
                resData.groups
              )
            ),
            catchError(this.handleError)
          )
      )
    )
  );

  authLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        tap(() => {
          this.authService.clearLogoutTimer();
          localStorage.removeItem('userData');
          resetGdprConsentCache();
          this.router.navigate(['/auth']);
        })
      ),
    { dispatch: false }
  );
}
